import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElementLayout,
} from '@anchorage/graphql/generated';
import { KYCQuestionRuleType } from '@anchorage/graphql/generated';
import type { CustomLinkProps } from '@anchorage/utils';
import { uuid } from '@anchorage/utils';
import { Button } from '@latitude/button';
import { Card } from '@latitude/card';
import { reactHookForm } from '@latitude/form';
import type { IconName } from '@latitude/icons';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import classNames from 'classnames';
import { useQuestionnaire } from 'hooks/useQuestionnaire/useQuestionnaire';
import type { ComponentType } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { QuestionNote } from '../QuestionNote';
import { GroupModal } from './components/GroupModal/GroupModal';
import { GroupIcon } from './GroupIcon';
import {
  getGroupIconFromTemplate,
  getGroupItemNameFromTemplate,
  getMinMaxFromRules,
  groupHasSomeAnswer,
  shouldShowEditButton,
} from './helpers';

// Round badge behind the group item icon. The badge turns white once the
// group has an answer, matching the item name's color change.
const GROUP_ITEM_ICON_CLASSES =
  'size-sizing-sys-size-40 shrink-0 rounded-full p-spacing-sys-spacing-8 text-color-ref-text-inverted';

type Props = {
  question: KYCQuestionDefinition;
  layout?: KYCQuestionnaireElementLayout;
  isDisabled?: boolean;
  answeredOptions?: Record<string, string>;
  CustomLink?: ComponentType<CustomLinkProps>;
  trackFn?: (questionId: string) => void;
  showTriggered?: boolean;
};

function Group({
  question,
  layout,
  isDisabled = false,
  answeredOptions,
  CustomLink,
  trackFn = () => {},
  showTriggered = false,
}: Props) {
  const { useFieldArray, useFormContext } = reactHookForm;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openedGroupIndex, setOpenedGroupIndex] = useState(0);
  const [openedGroupId, setOpenedGroupId] = useState('');

  const { getValues, control, getFieldState, trigger, watch } =
    useFormContext();
  const groupQuestions = question.group || [];
  const groupName = `${question.id}.group_answers`;

  // Extract minSize and maxSize from question.rules directly
  // This is needed for both Yup and non-Yup modes to control UI behavior
  const { min: minSize, max: maxSize } = getMinMaxFromRules(question.rules);

  const questionText = question.text;
  const questionHeader = question.header;

  const { fields, append, remove } = useFieldArray({
    name: groupName,
    control,
  });
  const numberOfGroups = fields.length;

  // Find all nested questions that have DISTINCT or MAX_SUM rules
  const questionsWithGroupRules = useMemo(() => {
    return question.group.filter((nestedQuestion) =>
      nestedQuestion.rules?.some(
        (rule) =>
          rule.type === KYCQuestionRuleType.DISTINCT ||
          rule.type === KYCQuestionRuleType.MAX_SUM,
      ),
    );
  }, [question.group]);

  useEffect(() => {
    if (questionsWithGroupRules.length === 0) {
      return;
    }
    // listen for changes in any of the questions that have DISTINCT or MAX_SUM rules
    const subscription = watch((_, { name }) => {
      if (name?.startsWith(groupName)) {
        // extract the question ID from the field name
        const id = name.split('.').slice(-2, -1)[0];
        const questionWitRule = questionsWithGroupRules.find(
          (nestedQuestion) => nestedQuestion.id === id,
        );
        // if the changed field is not one of the questions with rules, do nothing
        if (!questionWitRule) {
          return;
        }
        // trigger validation for all group entries for that question, so that
        // the errors show for all affected entries
        for (let index = 0; index < fields.length; index++) {
          const fieldPath = `${groupName}.${index}.${questionWitRule.id}.freeformText`;
          trigger(fieldPath);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [questionsWithGroupRules, groupName, watch, trigger, fields.length]);

  const { shouldShowElement } = useQuestionnaire();
  const shouldShow = useMemo(
    () =>
      shouldShowElement?.({
        triggerOptionID: question.triggerOptionID,
        answeredOptions,
      }),
    [question.triggerOptionID, shouldShowElement, answeredOptions],
  );

  /* add as many groups as the minimum required value */
  useEffect(() => {
    // hidden groups must not self-populate as that would cause rhf to try to validate them
    if (!shouldShow && !showTriggered) {
      return;
    }
    const diff = numberOfGroups - minSize;
    if (diff < 0) {
      append(Array.from({ length: Math.abs(diff) }, () => ({ id: uuid() })));
    }
  }, [numberOfGroups, minSize, append, fields, shouldShow, showTriggered]);

  const onAddDetailsClick = useCallback(
    ({ groupId, groupIndex }: { groupId: string; groupIndex: number }) => {
      setOpenedGroupIndex(groupIndex);
      setOpenedGroupId(groupId);
      setIsModalOpen(true);
    },
    [],
  );

  if (!shouldShow && !showTriggered) {
    return null;
  }

  const disableNewItems = !!layout?.groupTemplate?.newItemDisabled;

  const groupIconName = layout?.groupTemplate?.icon
    ? GroupIcon(layout.groupTemplate.icon)
    : null;

  return (
    <div className="mt-spacing-sys-spacing-16">
      {questionText && (
        <div className="mb-spacing-sys-spacing-16 flex flex-wrap justify-between">
          <Text variant="bodyMedium" color="strong">
            {questionText}
          </Text>
          {questionHeader ? (
            <Text
              className="mt-spacing-sys-spacing-4 w-full"
              variant="captionRegular"
              color="muted"
            >
              {questionHeader}
            </Text>
          ) : null}
          {question.optional && (
            <Text className="self-center" variant="captionMedium" color="muted">
              Optional
            </Text>
          )}
        </div>
      )}
      {question.note && (
        <QuestionNote
          note={question.note}
          className="mb-spacing-sys-spacing-16"
        />
      )}
      <div className="flex flex-col gap-spacing-sys-spacing-16">
        {fields.map(({ id: rowId }, groupIndex) => {
          const hasError = getFieldState(`${groupName}[${groupIndex}]`)?.error;
          const hasSomeAnswer = groupHasSomeAnswer({
            groupQuestions,
            groupName,
            groupIndex,
            getValues,
          });
          const { groupItemName, groupItemDescription } =
            getGroupItemNameFromTemplate({
              groupTemplate: layout?.groupTemplate,
              groupIndex,
              groupName,
              getValues,
            });
          const showEditButton = layout?.groupTemplate?.triggerOptionIdForModal
            ? shouldShowEditButton({
                triggerOptionIdForModal:
                  layout?.groupTemplate?.triggerOptionIdForModal,
                groupQuestions,
                groupName,
                groupIndex,
                getValues,
              })
            : true;

          const isFilled = hasSomeAnswer || !showEditButton;

          const groupItemNameClasses = classNames(
            'mr-auto flex items-center gap-spacing-sys-spacing-8',
            isFilled
              ? 'text-color-ref-text-strong'
              : 'text-color-ref-text-muted',
          );

          const groupItemIconClasses = classNames(
            GROUP_ITEM_ICON_CLASSES,
            isFilled
              ? 'bg-color-ref-shades-shade-0'
              : 'bg-color-ref-neutral-neutral-40',
          );

          const groupItemClasses = classNames(
            'relative flex-row items-center justify-end gap-spacing-sys-spacing-8 bg-color-comp-card-list-container-enabled',
            hasError && 'border-2 border-color-ref-ui-status-danger',
          );

          let groupIconNameFromTemplate: IconName | null = null;
          if (layout?.groupTemplate?.iconTemplate) {
            const iconNameFromTemplate = getGroupIconFromTemplate({
              iconTemplate: layout.groupTemplate.iconTemplate,
              groupIndex,
              groupName,
              getValues,
            });

            if (iconNameFromTemplate) {
              groupIconNameFromTemplate = GroupIcon(iconNameFromTemplate);
            }
          }

          const editButton = hasSomeAnswer ? (
            <Button
              htmlType="button"
              variant="strong"
              size="tiny"
              type="ghost"
              leadingElement={<Icon name="edit" title="Edit details" />}
              onClick={() => onAddDetailsClick({ groupId: rowId, groupIndex })}
            />
          ) : (
            <Button
              htmlType="button"
              variant="strong"
              size="tiny"
              type="outline"
              leadingElement={<Icon name="plus" />}
              onClick={() => onAddDetailsClick({ groupId: rowId, groupIndex })}
              className="shrink-0"
            >
              {layout?.groupTemplate?.addDetailsButtonTitle ||
                layout?.groupTemplate?.itemTitle ||
                ''}
            </Button>
          );

          return (
            <Card className={groupItemClasses} key={rowId}>
              <div
                className={groupItemNameClasses}
                data-question-id={question.id}
              >
                {groupIconName && !groupIconNameFromTemplate ? (
                  <Icon name={groupIconName} className={groupItemIconClasses} />
                ) : null}
                {groupIconNameFromTemplate ? (
                  <Icon
                    name={groupIconNameFromTemplate}
                    className={groupItemIconClasses}
                  />
                ) : null}
                <div className="flex flex-col gap-spacing-sys-spacing-4">
                  <Text className="w-full" variant="bodyBold">
                    {groupItemName}
                  </Text>
                  {groupItemDescription ? (
                    <Text variant="captionRegular" color="muted">
                      {groupItemDescription}
                    </Text>
                  ) : null}
                  {hasError ? (
                    <div className="flex gap-spacing-sys-spacing-8">
                      <Icon
                        name="alert-circle"
                        className="size-sizing-sys-size-16 text-color-ref-ui-status-danger"
                      />
                      <Text variant="captionMedium" color="danger">
                        Add or correct the information
                      </Text>
                    </div>
                  ) : null}
                </div>
              </div>
              {showEditButton ? editButton : null}
              {numberOfGroups > minSize && !isDisabled && !disableNewItems && (
                <Button
                  htmlType="button"
                  variant="neutral"
                  size="tiny"
                  type="ghost"
                  onClick={() => remove(groupIndex)}
                  data-testid="remove-set-button"
                  rawProps={{ 'aria-label': 'Remove' }}
                  leadingElement={<Icon name="x-close" title="Remove set" />}
                />
              )}
            </Card>
          );
        })}
      </div>
      {(maxSize === undefined || maxSize > numberOfGroups) &&
        !isDisabled &&
        !disableNewItems && (
          <Button
            htmlType="button"
            variant="neutral"
            size="tiny"
            type="ghost"
            leadingElement={<Icon name="plus" />}
            onClick={() => {
              const emptyQuestions: Record<string, null> = {};
              groupQuestions.map(
                (groupQuestion) => (emptyQuestions[groupQuestion.id] = null),
              );
              append({ id: uuid(), ...emptyQuestions });
            }}
            className="mt-spacing-sys-spacing-8 w-fit"
          >
            Add more
          </Button>
        )}
      <GroupModal
        title={layout?.groupTemplate?.itemTitle || ''}
        subtitle={layout?.groupTemplate?.itemSubtitle || ''}
        onModalClose={() => setIsModalOpen(false)}
        isModalOpen={isModalOpen}
        groupQuestions={groupQuestions}
        groupId={openedGroupId}
        groupNameAndIndex={`${groupName}.${openedGroupIndex}`}
        isGroupDisabled={isDisabled}
        answeredOptions={answeredOptions || {}}
        CustomLink={CustomLink}
        trackFn={trackFn}
      />
    </div>
  );
}

export { Group };
